import formatDate from '@utils/format-date'
import formatPaymentMethod from '@utils/format-payment-method'

const orderStatusTitleMap = {
  CANCEL: '已取消',
  PAID: '已付款',
  UNPAID: '未付款',
  PENDING_EVALUATION: '待評估',
};

const orderColorMap = {
  CANCEL: 'gray-1',
  PAID: 'primary',
  UNPAID: 'danger',
  PENDING_EVALUATION: 'warning',
};

const orderPriceColorMap = {
  CANCEL: 'gray-1',
  PAID: 'primary',
  UNPAID: 'danger',
  PENDING_EVALUATION: 'warning',
};

const orderInfoMap = {
  0: '預約取消',
  1: '預約確認',
  2: '預約保留',
  3: '預約確認',
  4: '預約確認',
  5: '預約確認',
  6: '預約確認',
};

const orderInfoColorMap = {
  CANCEL: 'warn',
  PAID: 'text',
  UNPAID: 'danger',
  PENDING_EVALUATION: 'warning',
};

/** 與後端 HotelOrder\Status::containerUnpaidTotalStatuses() 一致 */
const HOTEL_SUBORDER_PAYABLE_STATUSES = new Set(['PENDING', 'CONFIRMED', 'UNPAID', 'NO_SHOW'])

/** 子訂單狀態是否計入住宿容器應付金額 */
export function isHotelSuborderPayableStatus(status) {
  if (status == null || status === '') return false
  return HOTEL_SUBORDER_PAYABLE_STATUSES.has(String(status).toUpperCase())
}

/** 可計入應付之子訂單與容器 params 加總（僅作舊 API 無 allUnpaidTotal 時之後備） */
export function hotelPayableSubordersAndParamsTotal(container = {}) {
  const items = (container.orders && container.orders.items) || []
  let sum = 0
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (!item || !isHotelSuborderPayableStatus(item.status)) continue
    sum += Number(item.total || item.priceTotal || 0)
  }
  const params = container.params || []
  for (let j = 0; j < params.length; j++) {
    const p = params[j]
    sum += Number((p && p.total) || 0)
  }
  return sum
}

/**
 * 住宿容器是否仍有應付金額（與後端 OrderContainer::allUnpaidTotal 對齊）
 * 有 allUnpaidTotal 時優先採用；否則以可計入應付狀態之子訂單 + params 估算。
 */
export function hotelContainerNeedsPayment(container = {}) {
  if (container.type !== 'hotel') return true
  const raw = container.allUnpaidTotal
  if (raw != null && raw !== '') {
    const n = Number(raw)
    return !Number.isNaN(n) && n > 0
  }
  return hotelPayableSubordersAndParamsTotal(container) > 0
}

/** 住宿容器：任一子訂單 status 為 PENDING（待門市評估） */
export function isHotelOrderPendingEvaluation(orderItem = {}) {
  if (orderItem.type !== 'hotel') return false
  const items = (orderItem.orders && orderItem.orders.items) || []
  return items.some((item) => String(item.status).toUpperCase() === 'PENDING')
}

const orderInfoJoinMap = {
  0: '已取消',
  1: '預約確認',
  2: '預約保留',
  3: '即將參加',
  4: '參加中',
  5: '未參加',
  6: '已完成',
};

const orderInfoJoinColorMap = {
  0: 'Danger',
  1: 'Text',
  2: 'Danger',
  3: 'Indigo',
  4: 'Success',
  5: 'Danger',
  6: 'Text',
};

export function formatOrder(orderItem = {}) {
  if (isHotelOrderPendingEvaluation(orderItem)) {
    return {
      title: orderStatusTitleMap.PENDING_EVALUATION,
      color: orderColorMap.PENDING_EVALUATION,
      priceColor: orderPriceColorMap.PENDING_EVALUATION,
      info: orderStatusTitleMap.PENDING_EVALUATION,
      infoColor: orderInfoColorMap.PENDING_EVALUATION,
      paidInfo: formatOrderPaidInfo(orderItem),
    }
  }
  return {
    title: formatOrderStatusTitle(orderItem.status, orderItem.paymentStatus, orderItem.cancelDate, orderItem),
    color: formatOrderColor(orderItem.status, orderItem.paymentStatus, orderItem.cancelDate, orderItem),
    priceColor: formatOrderPriceColor(orderItem.status, orderItem.paymentStatus, orderItem.cancelDate, orderItem),
    info: formatOrderInfo(orderItem.status),
    infoColor: formatOrderInfoColor(orderItem.status, orderItem.paymentStatus, orderItem.cancelDate, orderItem),
    paidInfo: formatOrderPaidInfo(orderItem),
  }
}

export function formatOrderStatusTitle(status, paymentStatus, cancelDate, orderContext) {
  if (orderContext && isHotelOrderPendingEvaluation(orderContext)) {
    return orderStatusTitleMap.PENDING_EVALUATION
  }
  return orderStatusTitleMap[formatStatusToPaymentStatus(status, paymentStatus, cancelDate)]
}

export function formatOrderColor(status, paymentStatus, cancelDate, orderContext) {
  if (orderContext && isHotelOrderPendingEvaluation(orderContext)) {
    return orderColorMap.PENDING_EVALUATION
  }
  return orderColorMap[formatStatusToPaymentStatus(status, paymentStatus, cancelDate)]
}

export function formatOrderPriceColor(status, paymentStatus, cancelDate, orderContext) {
  if (orderContext && isHotelOrderPendingEvaluation(orderContext)) {
    return orderPriceColorMap.PENDING_EVALUATION
  }
  return orderPriceColorMap[formatStatusToPaymentStatus(status, paymentStatus, cancelDate)]
}

export function formatOrderInfo(status) {
  return orderInfoMap[status]
}

export function formatOrderInfoColor(status, paymentStatus, cancelDate, orderContext) {
  if (orderContext && isHotelOrderPendingEvaluation(orderContext)) {
    return orderInfoColorMap.PENDING_EVALUATION
  }
  return orderInfoColorMap[formatStatusToPaymentStatus(status, paymentStatus, cancelDate)]
}

export function formatOrderPaidInfo({
  status,
  paymentStatus,
  cancelDate,
  paidDate,
  paymentExpiredDate,
  paymentMethod,
  allowExpired = 1,
  type,
  orders,
} = {}) {
  if (isHotelOrderPendingEvaluation({ type, orders })) {
    return '待門市完成寵物評估後始可付款'
  }
  if(paymentStatus) {
    const orderPaidInfoMap = {
      CANCEL: `於${formatDate(cancelDate, 'dateWeekDay')} 取消`,
      PAID: `${formatDate(paidDate, 'dateWeekDay') || ''} ${formatPaymentMethod(paymentMethod)}`,
      UNPAID: paymentExpiredDate ? `請於${formatDate(paymentExpiredDate, 'yyyy/MM/dd HH:mm')}前付款` : '請前往付款',
    };

    if(allowExpired && paymentStatus === 'UNPAID') {
      return '請盡快付款'
    } else if(paymentMethod === 'Prepaid' && paymentStatus === 'UNPAID') {
      return paymentExpiredDate ? `請於${formatDate(paymentExpiredDate, 'yyyy/MM/dd HH:mm')}前確認` : '請前往付款';
    } else {
      return orderPaidInfoMap[formatStatusToPaymentStatus(status, paymentStatus, cancelDate)];
    }
  }
}

export function formatStatusToPaymentStatus(status, paymentStatus, cancelDate) {
  if(status === 0 || !!cancelDate) {
    return 'CANCEL'
  } else if(!paymentStatus) {
    if(status === 2) {
      return 'UNPAID'
    } else {
      return 'PAID'
    }
  } else {
    return paymentStatus
  }
}

// 參加狀態
export function formatOrderJoin(orderItem = {}) {
  return {
    title: formatOrderJoinStatusTitle(orderItem.status, orderItem.paymentStatus),
    color: formatOrderJoinColor(orderItem.status, orderItem.paymentStatus),
  }
}
export function formatOrderJoinStatusTitle(status, containerPaymentStatus) {
  let orderStatus = status
  if(containerPaymentStatus) {
    orderStatus = 2
  }
  return orderInfoJoinMap[orderStatus]
}

export function formatOrderJoinColor(status, containerPaymentStatus) {
  let orderStatus = status
  if(containerPaymentStatus) {
    orderStatus = 2
  }
  return orderInfoJoinColorMap[orderStatus]
}
